import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectVideos } from "../../store/videosSlice";
import "./VideoPage.css";
import arrowDown from "../../assets/arrowDown.svg";
import shareIcon from "../../assets/shareIcon.svg";
import BackButton from "../BackButton/BackButton";
import VideoControls from "../VideoControls/VideoControls";
import CommentsLayout from "../CommentsBlock/CommentsLayout";
import VideoWithHLS from "../VideoWithHls/VideoWithHls";
import SharePopup from "./SharePopup";
import UsersPopup from "./UsersPopup";
import BacklogPopup from "./BacklogPopup";
import {
  createVersionComment,
  deleteVersionComment,
  fetchProjectUsers,
  fetchVersion,
  fetchVersionComments,
  forceDeleteVersionComment,
  restoreVersionComment,
  updateVersionComment,
} from "../../store/projectsSlice";
import Avatar from "../Avatar/Avatar";
import {
  currentOrganizationOwnerSelector,
  currentOrganizationSelector,
  currentUploadingVideosSelector,
  organizationUsersSelector,
  redirectingFromSharingSelector,
  userSelector,
} from "../../store/selectors";
import { useTranslation } from "react-i18next";
import { processShareToken } from "../../store/usersSlice";
import { useAuth } from "../../context/AuthContext";
import ReleaseNotesModal from "../ReleaseNotesModal/ReleaseNotesModal";
import InfoModal from "../InfoModal/InfoModal";
import {
  setCurrentUploadingVideosInfo,
  setRedirectingFromSharing,
} from "../../store/appSlice";
import VideoTimeline from "../VideoTimeline/VideoTimeline";
import { ClickAwayListener } from "@mui/material";
import useIsMobile from "../../hooks/useIsMobile";

function VideoPage() {
  const { versionId } = useParams();
  const { sharedToken } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const user = useSelector(userSelector);
  const currentOrganization = useSelector(currentOrganizationSelector);
  const [newMarkerInfo, setNewMarkerInfo] = useState(null);
  const organizationUsers = useSelector(organizationUsersSelector);
  const [isSharedChild, setIsSharedChild] = useState(false);
  const [tagsArray, setTagsArray] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const videos = useSelector(selectVideos);
  const { t, i18n } = useTranslation();
  const [video, setVideo] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const orgOwner = useSelector(currentOrganizationOwnerSelector);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [isWideMode, setIsWideMode] = useState(false);
  const [commentsArray, setCommentsArray] = useState([]);
  const [videoControls, setVideoControls] = useState(null);
  const [playbackStarted, setPlaybackStarted] = useState(null);
  const playerRef = useRef(null);
  const [isSharePopupOpen, setSharePopupOpen] = useState(false);
  const [isUsersPopupOpen, setUsersPopupOpen] = useState(false);
  const [isBacklogPopupOpen, setBacklogPopupOpen] = useState(false);
  const [markerPoint, setMarkerPoint] = useState(null);
  const [isLocationMode, setIsLocationMode] = useState(false);
  const [commentsFilter, setCommentsFilter] = useState("all");
  const [isShowUnreplied, setIsShowUnreplied] = useState(false);
  const [highlightCommentId, setHighlightCommentId] = useState(null);
  const [currentTags, setCurrentTags] = useState([]);
  const [isShowReleaseNotes, setIsShowReleaseNotes] = useState(false);
  const redirectingFromSharing = useSelector(redirectingFromSharingSelector);
  const [redirectedFromShare, setRedirectedFromShare] = useState(false);
  const [currentHoveredCommentId, setCurrentHoveredCommentId] = useState(null);
  const [newCommentEnd, setNewCommentEnd] = useState(null);
  const [isVideoWaiting, setIsVideoWaiting] = useState(false);
  const [isSendingComment, setIsSendingComment] = useState(false);
  const [myProjectRole, setMyProjectRole] = useState(null);
  const [isSharedNeedLoginVisible, setIsSharedNeedLoginVisible] =
    useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const isShared = !!sharedToken;
  const currentUploadingVideosInfo = useSelector(
    currentUploadingVideosSelector
  );
  const isViewerRole =
    isViewer || (myProjectRole && myProjectRole.role === "VIEWER");
  console.log(
    "VIEWER ROLE IS ",
    isViewerRole,
    ", BECAUSE isViewer=",
    isViewer,
    ", AND CURRENT PROJECT ROLE = ",
    myProjectRole?.role
  );

  const handleLocationClick = (e) => {
    const videoElement = videoControls?.getVideoElement?.();
    if (!videoElement) return;

    const rect = videoElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;

    setMarkerPoint({ x: xPercent, y: yPercent });
  };

  const calculateCurrentTags = (commentsArray) => {
    const arr = commentsArray || [];
    const resArray = arr.reduce((acc, current) => {
      if (current.tags.length) {
        return [...acc, ...current.tags.map((tag) => tag.id)];
      }
      return acc;
    }, []);
    setCurrentTags(resArray);
  };

  useEffect(() => {
    const uploadingVideo = currentUploadingVideosInfo.find(
      (uploadingVid) => uploadingVid.data.id === +versionId
    );
    if (uploadingVideo && uploadingVideo.isCompleted) {
      const vidIndex = currentUploadingVideosInfo.findIndex(
        (vid) => vid.data.id === versionId
      );
      const newUploadingVideosArray = [...currentUploadingVideosInfo];
      newUploadingVideosArray.splice(vidIndex, 1);
      dispatch(setCurrentUploadingVideosInfo(newUploadingVideosArray));
    }
  }, []);

  useEffect(() => {
    if (redirectingFromSharing) {
      setRedirectedFromShare(true);
    }
    if (window.location.href.includes("role=viewer")) {
      setIsViewer(true);
    } else {
      setIsViewer(false);
    }
    if (sharedToken) {
      dispatch(processShareToken(sharedToken)).then((res) => {
        if (!res.error) {
          if (res.payload.id !== +versionId) {
            setIsSharedChild(true);
            dispatch(fetchVersion(versionId)).then((res2) => {
              const videoRes = res2.payload;
              setVideo(videoRes);
              if (res.payload.releaseNotes) {
                setIsShowReleaseNotes(true);
              }
              dispatch(
                fetchVersionComments({
                  versionId,
                  isDeleted: commentsFilter === "archived",
                })
              ).then((res) => {
                setCommentsArray(res.payload);
                calculateCurrentTags(res.payload);
              });
              console.log(res2);
            });
          } else {
            setVideo(res.payload);
            setCommentsArray(res.payload.comments);
            if (res.payload.releaseNotes) {
              setIsShowReleaseNotes(true);
            }
            dispatch(setRedirectingFromSharing(false));
          }
        }
      });

      return;
    }
    dispatch(fetchVersion(versionId)).then((res) => {
      const videoRes = res.payload;
      const projectId = videoRes.projectId;
      setVideo(videoRes);
      if (redirectingFromSharing) {
        dispatch(setRedirectingFromSharing(false));
        if (videoRes.releaseNotes) {
          setIsShowReleaseNotes(true);
        }
      }
      dispatch(fetchProjectUsers(projectId)).then((res) => {
        if (!res.error) {
          let usersArray = res.payload;
          let myRole = usersArray.find((usr) => usr.userId === user.userId);
          if (!myRole && user) {
            const idsToCheck = videoRes.path;
            for (let i = idsToCheck.length - 1; i >= 0; i--) {
              console.log("checking project roles to find id");
              const projectId = idsToCheck[i].id;
              const parentRole = user.projectRoles.find(
                (role) => role.projectId === projectId
              );
              if (parentRole) {
                myRole = {
                  id: parentRole.id,
                  role: parentRole.role,
                  roleSourceProjectId: parentRole.projectId,
                };
                break;
              }
            }
          }
          if (myRole) {
            setMyProjectRole(myRole);
          }

          const filteredArray = usersArray.filter((usr) => {
            if (!usr.allowedContentVersionIds) return true;
            if (!usr.allowedContentVersionIds.length) return true;
            if (!videoRes) return false;
            return usr.allowedContentVersionIds.includes(videoRes.id);
          });
          setProjectUsers(filteredArray);
        }
      });
    });
    dispatch(
      fetchVersionComments({
        versionId,
        isDeleted: commentsFilter === "archived",
      })
    ).then((res) => {
      setCommentsArray(res.payload);
      calculateCurrentTags(res.payload);
    });
  }, []);

  useEffect(() => {
    if (sharedToken) {
      return;
    }
    dispatch(
      fetchVersionComments({
        versionId,
        isDeleted: commentsFilter === "archived",
      })
    ).then((res) => {
      setCommentsArray(res.payload);
      calculateCurrentTags(res.payload);
    });
  }, [commentsFilter]);

  const changeCommentsFilter = (value) => {
    setCommentsFilter(value);
  };

  useEffect(() => {
    const updateControls = () => {
      if (playerRef.current) {
        setVideoControls({
          ...playerRef.current.controls,
          toggleWide: () => {
            const newWideMode = !isWideMode;
            setIsWideMode(newWideMode);
            return newWideMode;
          },
        });
      }
    };
    updateControls();
  }, [playerRef.current, isWideMode]);

  const filteredTags = tagsArray.filter((tag) => currentTags.includes(tag.id));

  const updateComments = () => {
    dispatch(
      fetchVersionComments({
        versionId,
        isDeleted: commentsFilter === "archived",
      })
    ).then((res) => {
      setCommentsArray(res.payload);
    });
  };

  const updateVideo = () => {
    dispatch(fetchVersion(versionId)).then((res) => {
      const videoRes = res.payload;
      const projectId = videoRes.projectId;
      setVideo(videoRes);
    });
  };

  const handleTimeUpdate = useCallback(
    (time) => {
      if (typeof time === "number" && !isNaN(time) && time >= 0) {
        setCurrentTime(time);
        if (totalTime > 0) {
          setCurrentProgress((time / totalTime) * 100);
        }
      }
    },
    [totalTime]
  );

  const getVideoTitle = () => {
    if (!video) return "Loading...";
    if (!video.path) return video.version;
    const videoPath = video.path;
    let videoTitle = videoPath.reduce((res, current) => {
      res += `${current.title} • `;
      return res;
    }, "");

    videoTitle += `${t("video.version")} ${video.version}`;
    return videoTitle;
  };

  const handleDurationChange = useCallback(
    (duration) => {
      if (typeof duration === "number" && !isNaN(duration) && duration > 0) {
        setTotalTime(duration);
        if (currentTime >= 0) {
          setCurrentProgress((currentTime / duration) * 100);
        }
      }
    },
    [currentTime]
  );

  if (!video) {
    return (
      <div className="App">
        <div className="mainContent">
          <div className="leftContent">
            <div className="videoPageHeader">
              <BackButton
                onClick={() => {
                  if (redirectedFromShare) {
                    navigate("/home");
                  } else {
                    navigate(-1);
                  }
                }}
              />
              <div
                style={{
                  flex: 1,
                  textAlign: "center",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: 500,
                  fontFamily: "NT Somic, sans-serif",
                }}
              >
                Loading...
              </div>
              <div style={{ width: "56px" }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleCreateComment = async (commentData, isEdit) => {
    setIsSendingComment(true);
    if (isEdit) {
      const commentId = commentData.id;
      const finalCommentData = {
        content: commentData.content,
        tagIds: commentData.tagIds || [],
        mentions: commentData.mentions || [],
        authorId: commentData.authorId,
        timecode: commentData.timecode,
        extractMentions: false,
      };
      const res = await dispatch(
        updateVersionComment({ commentId, commentData: finalCommentData })
      );

      setIsSendingComment(false);

      if (!res.error) {
        dispatch(
          fetchVersionComments({
            versionId,
            isDeleted: commentsFilter === "archived",
          })
        ).then((result) => {
          if (!result.error) {
            setCommentsArray(result.payload);
            calculateCurrentTags(result.payload);
          }
        });
      }
      return res;
    }

    dispatch(createVersionComment({ versionId, commentData })).then((res) => {
      if (!res.error) {
        dispatch(
          fetchVersionComments({
            versionId,
            isDeleted: commentsFilter === "archived",
          })
        ).then((result) => {
          if (!result.error) {
            setCommentsArray(result.payload);
            calculateCurrentTags(result.payload);
          }
        });
      }
      setIsSendingComment(false);
    });
  };

  const handleDeleteComment = async (commentId) => {
    const res = await dispatch(deleteVersionComment({ versionId, commentId }));
    if (!res.error) {
      dispatch(
        fetchVersionComments({
          versionId,
          isDeleted: commentsFilter === "archived",
        })
      ).then((result) => {
        if (!result.error) {
          setCommentsArray(result.payload);
          calculateCurrentTags(result.payload);
        }
      });
    }
    return res;
  };

  const handleRestoreComment = (commentId) => {
    setCommentsFilter("all");
    dispatch(restoreVersionComment({ versionId, commentId })).then((res) => {
      if (!res.error) {
        dispatch(
          fetchVersionComments({
            versionId,
            isDeleted: false,
          })
        ).then((result) => {
          if (!result.error) {
            setCommentsArray(result.payload);
            calculateCurrentTags(result.payload);
          }
        });
      }
    });
  };

  const handleForceDeleteComment = async (commentId) => {
    const res = await dispatch(
      forceDeleteVersionComment({ versionId, commentId })
    );

    if (!res.error) {
      const res2 = await dispatch(
        fetchVersionComments({
          versionId,
          isDeleted: commentsFilter === "archived",
        })
      );

      if (!res2.error) {
        setCommentsArray(res2.payload);
        calculateCurrentTags(res2.payload);
      }
    }

    return res;
  };

  const renderUsersBlock = () => {
    let sortedOrganizationUsers = [...projectUsers];
    sortedOrganizationUsers = sortedOrganizationUsers
      .sort((a, b) => (a.role === "OWNER" ? 1 : -1))
      .slice(-3);
    if (isMobile) {
      sortedOrganizationUsers = sortedOrganizationUsers[0]
        ? [sortedOrganizationUsers[0]]
        : [];
    }

    return (
      <div className="project-users-row">
        {sortedOrganizationUsers.map((userItem, index) => (
          <div
            className="project-user-avatar-wrapper"
            style={{ right: isMobile ? -13 : `${index * 25}px` }}
          >
            <Avatar name={userItem.email} size={32} />
          </div>
        ))}
      </div>
    );
  };

  const handleUpdateTeamMembers = () => {
    let projectId = video.projectId;
    dispatch(fetchProjectUsers(projectId)).then((res) => {
      if (!res.error) {
        let usersArray = res.payload;
        var unique = usersArray.filter(
          (value, index, array) =>
            array.findIndex((val) => val.email === value.email) === index
        );
        setProjectUsers(unique);
      }
    });
  };

  return (
    <div className={isMobile ? "App-mobile" : "App"}>
      {isMobile && (
        <div
          className={isMobile ? "videoPageHeader-mobile" : "videoPageHeader"}
          style={{ justifyContent: isShared ? "center" : "space-between" }}
        >
          {(!isShared && !isViewer) || (isViewer && isSharedChild) ? (
            <BackButton
              onClick={() => {
                if (isViewer && isSharedChild) {
                  navigate(-1);
                  return;
                }
                if (redirectedFromShare) {
                  navigate("/home");
                } else {
                  navigate(-1);
                }
              }}
            />
          ) : null}
          {isViewer && <div style={{ width: 5, height: 5 }}></div>}
          {isShared && !isViewer && (
            <div style={{ display: "flex", position: "absolute", left: 20 }}>
              <span
                style={{
                  color: "white",
                  width: 250,
                  fontSize: 14,
                  textAlign: "left",
                  fontFamily: "NT Somic",
                  fontWeight: 500,
                }}
              >
                Войдите или зарегистрируйтесь, чтобы поучаствовать в обсуждении
              </span>
              <button
                style={{
                  backgroundColor: "#4477ff",
                  border: "none",
                  padding: 10,
                  color: "white",
                  marginLeft: 15,
                  borderRadius: 80,
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
              >
                Войти
              </button>
            </div>
          )}

          <div style={{ position: "relative" }}>
            <div
              style={{
                textAlign: "center",
                color: "white",
                fontSize: "16px",
                fontWeight: 500,
                fontFamily: "NT Somic, sans-serif",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                gap: "8px",
                position: "relative",
                zIndex: 2,
                paddingRight: isMobile ? 15 : 0,
              }}
              onClick={() => {
                if (!isShared) {
                  setSharePopupOpen((v) => !v);
                }
              }}
            >
              {getVideoTitle()}
              {!isShared && !isMobile && (
                <img
                  src={shareIcon}
                  alt="arrow"
                  style={{
                    width: 20,
                    height: 20,
                    // transform: isSharePopupOpen ? "rotate(180deg)" : "none",
                  }}
                />
              )}
            </div>
            {isSharePopupOpen && (
              <SharePopup
                video={video}
                updateVideo={updateVideo}
                onClose={() => setSharePopupOpen(false)}
                onOpenBacklog={() => setBacklogPopupOpen(true)}
                className="share-popup-context-menu-absolute"
              />
            )}
          </div>
          {isBacklogPopupOpen && (
            <BacklogPopup
              isOpen={isBacklogPopupOpen}
              onClose={() => setBacklogPopupOpen(false)}
            />
          )}
          {isSharedNeedLoginVisible && (
            <InfoModal
              actionText={"Войти"}
              action={() => {
                window.location.href = "https://go.7cut.video";
              }}
              title="Необходим вход"
              text="Войдите или зарегистрируйтесь, чтобы поучаствовать в обсуждении"
              isOpen={isSharedNeedLoginVisible}
              onClose={() => setIsSharedNeedLoginVisible(false)}
            />
          )}
          {isUsersPopupOpen && (
            <UsersPopup
              video={video}
              projectInfo={video}
              isViewer={isViewerRole}
              isOpen={isUsersPopupOpen}
              onClose={() => setUsersPopupOpen(false)}
              usersArray={projectUsers}
              updateUsersArray={handleUpdateTeamMembers}
            />
          )}
          {!isShared && (
            <div
              style={{
                textAlign: "center",
                color: "white",
                fontSize: "16px",
                height: "100%",
                fontWeight: 500,
                fontFamily: "NT Somic, sans-serif",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                gap: "8px",
              }}
              onClick={() => setUsersPopupOpen((v) => !v)}
            >
              {renderUsersBlock()}
            </div>
          )}
        </div>
      )}
      <div
        className={`${isMobile ? "mainContent-mobile" : "mainContent"} ${
          isWideMode ? "wide-mode" : ""
        }`}
      >
        <div className={isMobile ? "leftContent-mobile" : "leftContent"}>
          {!isWideMode && !isMobile && (
            <div
              className={
                isMobile ? "videoPageHeader-mobile" : "videoPageHeader"
              }
              style={{
                justifyContent:
                  !isShared || (isViewer && isSharedChild)
                    ? "space-between"
                    : "center",
              }}
            >
              {((!isShared && !isViewer) || (isViewer && isSharedChild)) && (
                <BackButton
                  onClick={() => {
                    if (isViewer && isSharedChild) {
                      navigate(-1);
                      return;
                    }
                    if (redirectedFromShare) {
                      navigate("/home");
                    } else {
                      navigate(-1);
                    }
                  }}
                />
              )}
              {isViewer && !isSharedChild && (
                <div style={{ width: 5, height: 5 }}></div>
              )}
              {isShared && !isViewer && (
                <div
                  style={{ display: "flex", position: "absolute", left: 20 }}
                >
                  <span
                    style={{
                      color: "white",
                      width: 250,
                      fontSize: 14,
                      textAlign: "left",
                      fontFamily: "NT Somic",
                      fontWeight: 500,
                    }}
                  >
                    Войдите или зарегистрируйтесь, чтобы поучаствовать в
                    обсуждении
                  </span>
                  <button
                    style={{
                      backgroundColor: "#4477ff",
                      border: "none",
                      padding: 10,
                      color: "white",
                      marginLeft: 15,
                      borderRadius: 80,
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/")}
                  >
                    Войти
                  </button>
                </div>
              )}

              <div style={{ position: "relative" }}>
                <div
                  style={{
                    textAlign: "center",
                    color: "white",
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: "NT Somic, sans-serif",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    gap: "8px",
                    position: "relative",
                    zIndex: 2,
                    paddingRight: isMobile ? 12 : 0,
                  }}
                  onClick={() => {
                    if (myProjectRole && myProjectRole.role === "VIEWER")
                      return;
                    if (!isShared) {
                      setSharePopupOpen((v) => !v);
                    }
                  }}
                >
                  {getVideoTitle()}
                  {!isShared && !isMobile && !isViewerRole && (
                    <img
                      src={shareIcon}
                      alt="arrow"
                      style={{
                        width: 20,
                        height: 20,
                        // transform: isSharePopupOpen ? "rotate(180deg)" : "none",
                      }}
                    />
                  )}
                </div>
                {isSharePopupOpen && (
                  <SharePopup
                    video={video}
                    updateVideo={updateVideo}
                    onClose={() => setSharePopupOpen(false)}
                    onOpenBacklog={() => setBacklogPopupOpen(true)}
                    className="share-popup-context-menu-absolute"
                  />
                )}
              </div>
              {isBacklogPopupOpen && (
                <BacklogPopup
                  isOpen={isBacklogPopupOpen}
                  onClose={() => setBacklogPopupOpen(false)}
                />
              )}
              {isSharedNeedLoginVisible && (
                <InfoModal
                  actionText={"Войти"}
                  action={() => {
                    window.location.href = "https://go.7cut.video";
                  }}
                  title="Необходим вход"
                  text="Войдите или зарегистрируйтесь, чтобы поучаствовать в обсуждении"
                  isOpen={isSharedNeedLoginVisible}
                  onClose={() => setIsSharedNeedLoginVisible(false)}
                />
              )}
              {isUsersPopupOpen && (
                <UsersPopup
                  video={video}
                  projectInfo={video}
                  isViewer={isViewerRole}
                  isOpen={isUsersPopupOpen}
                  onClose={() => setUsersPopupOpen(false)}
                  usersArray={projectUsers}
                  updateUsersArray={handleUpdateTeamMembers}
                />
              )}
              {(!isShared && (
                <div
                  style={{
                    textAlign: "center",
                    color: "white",
                    fontSize: "16px",
                    height: "100%",
                    fontWeight: 500,
                    fontFamily: "NT Somic, sans-serif",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    gap: "8px",
                  }}
                  onClick={() => setUsersPopupOpen((v) => !v)}
                >
                  {renderUsersBlock()}
                </div>
              )) ||
                null}
              {isViewer && isSharedChild && (
                <div style={{ width: 56, height: 5 }}></div>
              )}
            </div>
          )}
          <div className="videoSection">
            <div className="videoPlayerContainer">
              <VideoWithHLS
                setPlaybackStarted={setPlaybackStarted}
                ref={playerRef}
                hlsUrl={video.hlsUrl}
                isVideoWaiting={isVideoWaiting}
                setIsVideoWaiting={setIsVideoWaiting}
                onTimeUpdate={handleTimeUpdate}
                resetMarkerPoint={() => setMarkerPoint(null)}
                onDurationChange={handleDurationChange}
                isWideMode={isWideMode}
                markerPoint={markerPoint}
                isLocationMode={isLocationMode}
                onLocationClick={handleLocationClick}
              />
            </div>
            <div
              className={isMobile ? "videoTimeline-mobile" : "videoTimeline"}
            >
              <VideoTimeline
                isViewer={isViewerRole}
                playbackStarted={playbackStarted}
                setPlaybackStarted={setPlaybackStarted}
                newMarkerInfo={newMarkerInfo}
                setNewMarkerInfo={setNewMarkerInfo}
                currentTime={currentTime}
                totalTime={totalTime}
                progress={currentProgress}
                newCommentEnd={newCommentEnd}
                setNewCommentEnd={(timecodeEnd) =>
                  setNewCommentEnd(timecodeEnd)
                }
                videoControls={videoControls}
                isWideMode={isWideMode}
                onWideModeChange={setIsWideMode}
                comments={commentsArray}
                onCommentMarkerClick={setHighlightCommentId}
                currentHoveredCommentId={currentHoveredCommentId}
                setCurrentHoveredCommentId={setCurrentHoveredCommentId}
              />
            </div>
          </div>
          <div className="videoControls-buttons">
            <VideoControls
              currentTime={currentTime}
              totalTime={totalTime}
              progress={currentProgress}
              videoControls={videoControls}
              isWideMode={isWideMode}
              onWideModeChange={setIsWideMode}
              comments={commentsArray}
              onCommentMarkerClick={setHighlightCommentId} // NEW PROP
            />
          </div>
        </div>

        {isShowReleaseNotes && (
          <ReleaseNotesModal
            isOpen={isShowReleaseNotes}
            onClose={() => setIsShowReleaseNotes(false)}
            video={video}
          />
        )}
        {!isWideMode && (
          <div
            className={isMobile ? "commentsSection-mobile" : "commentsSection"}
          >
            <CommentsLayout
              newMarkerInfo={newMarkerInfo}
              setNewMarkerInfo={setNewMarkerInfo}
              updateComments={updateComments}
              isSendingComment={isSendingComment}
              commentsFilter={commentsFilter}
              onChangeCommentsFilter={changeCommentsFilter}
              onDeleteComment={handleDeleteComment}
              onForceDeleteComment={handleForceDeleteComment}
              onRestoreComment={handleRestoreComment}
              videoControls={videoControls}
              currentTime={currentTime}
              videoRef={playerRef}
              comments={commentsArray}
              onCommentsChange={setCommentsArray}
              markerPoint={markerPoint}
              onSetMarkerPoint={setMarkerPoint}
              isLocationMode={isLocationMode}
              highlightCommentId={highlightCommentId}
              setIsLocationMode={setIsLocationMode}
              onPressTimestamp={(coords) => setMarkerPoint(coords)}
              onSubmitComment={handleCreateComment}
              isShowUnreplied={isShowUnreplied}
              setIsShowUnreplied={setIsShowUnreplied}
              versionId={versionId}
              projectUsers={projectUsers}
              tagsArray={tagsArray}
              setTagsArray={setTagsArray}
              filteredTags={filteredTags}
              setCurrentHoveredCommentId={setCurrentHoveredCommentId}
              currentHoveredCommentId={currentHoveredCommentId}
              isShared={isShared}
              onShowSharedNeedLogin={() => setIsSharedNeedLoginVisible(true)}
              isViewer={isViewerRole}
              myProjectRole={myProjectRole}
              newCommentEnd={newCommentEnd}
              setNewCommentEnd={setNewCommentEnd}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default VideoPage;
